// Fleet → CI (P-004 milestone 2), owners only: the runner strip, recent runs (a row expands into
// its jobs and their steps, with "Log on GitHub"), and trends per job over 7, 30 or 90 days. The
// runs poll every 5 s while the page is visible; trends every minute (they move slowly).
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, type CiJob, type CiResult, type CiRun, type CiRunDetail, type CiTrend, type Fleet, type FleetRunner } from '../api/client';
import { recordPath, settingsSectionPath } from '../lib/routes';
import { ago } from '../lib/time';
import { useLoad } from '../lib/useLoad';
import { usePoll } from '../lib/usePoll';
import { RunnerBadge } from './Fleet';
import { Badge, Bars, Callout, EmptyState, ExpandableRow, Select, Table, Tabs, Th, buttonClass, cn, mono, type BadgeTone } from './ui';

const META = 'text-[12px] text-ink-muted';
const PAGE = 25;

const RESULT: Record<CiResult, [BadgeTone, string]> = {
  queued: ['muted', 'Queued'],
  running: ['signal', 'Running'],
  passed: ['success', 'Passed'],
  failed: ['danger', 'Failed'],
  timed_out: ['warning', 'Timed out'],
  cancelled: ['muted', 'Cancelled'],
  skipped: ['muted', 'Skipped'],
  neutral: ['muted', 'Neutral'],
  action_required: ['warning', 'Needs approval'],
  stale: ['muted', 'Stale'],
  unknown: ['muted', 'Lost track'],
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'passed', label: 'Passed' },
  { id: 'failed', label: 'Failed' },
  { id: 'running', label: 'Running' },
  { id: 'cancelled', label: 'Cancelled' },
];
type Filter = 'passed' | 'failed' | 'running' | 'cancelled';

const WINDOWS = [
  { id: '7', label: '7 days' },
  { id: '30', label: '30 days' },
  { id: '90', label: '90 days' },
];

/** Seconds as "44s", "6m 31s", "1h 05m". */
export function took(s: number | null | undefined): string {
  if (s === null || s === undefined) return '–';
  const t = Math.round(s);
  if (t < 60) return `${t}s`;
  if (t < 3600) return `${Math.floor(t / 60)}m ${String(t % 60).padStart(2, '0')}s`;
  return `${Math.floor(t / 3600)}h ${String(Math.floor((t % 3600) / 60)).padStart(2, '0')}m`;
}

function runTook(r: { duration_s: number | null; started_at: string | null; result: CiResult }, now: number): string {
  if (r.duration_s !== null) return took(r.duration_s);
  if (r.result === 'running' && r.started_at) return took((now - Date.parse(r.started_at)) / 1000);
  return '–';
}

const short = (repo: string) => repo.split('/').pop() ?? repo;

export function ResultBadge({ result, superseded, stalled }: { result: CiResult; superseded?: boolean; stalled?: boolean }) {
  if (superseded && result === 'cancelled') return <Badge>Superseded</Badge>;
  if (stalled && result === 'queued') return <Badge tone="warning">Stalled</Badge>;
  const [tone, text] = RESULT[result];
  return (
    <Badge tone={tone} spinner={result === 'running'}>
      {text}
    </Badge>
  );
}

function Branch({ run, ws }: { run: CiRun; ws: number }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      {run.item && (
        <Link
          to={recordPath(ws, run.item.repo_id, run.item.key)}
          className="flex-none rounded-[5px] border border-hairline-strong px-1.5 font-mono text-[11px] font-semibold text-ink-display hover:border-accent-display hover:text-accent-display hover:no-underline"
          aria-label={`${run.item.key}: ${run.item.title}`}
        >
          {run.item.key}
        </Link>
      )}
      <span className={cn(mono, 'min-w-0 truncate text-[11.5px]')} title={run.branch ?? undefined}>
        {run.branch ?? '–'}
      </span>
    </span>
  );
}

function Commit({ run }: { run: CiRun }) {
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      {run.head_sha && <span className={cn(mono, 'flex-none text-[11px] text-ink-muted')}>{run.head_sha.slice(0, 7)}</span>}
      <span className="min-w-0 truncate" title={run.commit_message ?? undefined}>
        {run.commit_message ?? run.workflow ?? ''}
      </span>
    </span>
  );
}

// ------------------------------------------------------------------ runner strip

function RunnerStrip({ runners }: { runners: FleetRunner[] }) {
  if (runners.length === 0) return null;
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(210px,1fr))] gap-2" aria-label="Runners" role="group">
      {runners.map((r) => (
        <div key={r.name} className="flex min-w-0 flex-col gap-0.5 rounded border border-hairline px-3 py-2" data-runner={r.name}>
          <div className="flex items-center justify-between gap-2">
            <b className={cn(mono, 'min-w-0 truncate text-[12px] font-medium text-ink-display')}>{r.name}</b>
            <RunnerBadge runner={r} />
          </div>
          <span className={cn(META, 'truncate')}>
            {[r.host?.name, r.github?.source].filter(Boolean).join(' · ') || 'Not matched'}
          </span>
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ one run's jobs and steps

function JobCard({ job, now }: { job: CiJob; now: number }) {
  const running = job.result === 'running';
  const t = job.duration_s ?? (running && job.started_at ? (now - Date.parse(job.started_at)) / 1000 : null);
  return (
    <div className="rounded border border-hairline bg-page px-3 py-2.5" data-job={job.name}>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <b className="font-semibold text-ink-display">{job.name}</b>
        <ResultBadge result={job.result} />
        <span className={META}>
          {took(t)}
          {job.runner_name && (
            <>
              {' · on '}
              <span className={mono}>{job.runner_name}</span>
            </>
          )}
        </span>
        <span className="grow" />
        {job.html_url && (
          <a className="text-[12px] text-accent-display" href={job.html_url} target="_blank" rel="noreferrer">
            Log on GitHub ↗
          </a>
        )}
      </div>
      {job.steps.length > 0 && (
        <ol className="m-0 mt-2 grid list-none grid-cols-[28px_minmax(0,1fr)_auto] gap-x-2.5 gap-y-0.5 p-0 text-[12px]">
          {job.steps.map((s) => {
            const failed = s.conclusion === 'failure';
            return (
              <li key={s.number} className="contents">
                <span className={cn(mono, 'pt-px text-right text-[11px] text-ink-muted')}>{s.number}</span>
                <span className={cn('min-w-0 truncate', failed ? 'font-medium text-danger-ink' : s.conclusion === 'skipped' ? 'text-ink-muted' : '')}>
                  {s.name}
                  {s.status === 'in_progress' && <span className="text-ink-muted"> · running</span>}
                </span>
                <span className="text-ink-muted tabular-nums">{s.duration_s !== null ? took(s.duration_s) : ''}</span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function RunDetail({ ws, run, now }: { ws: number; run: CiRun; now: number }) {
  const open = run.result === 'running' || run.result === 'queued';
  const poll = usePoll(
    () => api.GET('/api/workspaces/{ws_id}/fleet/ci/runs/{run_id}', { params: { path: { ws_id: ws, run_id: run.id } } }),
    `${ws}:${run.id}`,
    open ? 5000 : 600_000,
  );
  if (poll.result.state === 'loading') {
    return (
      <p className={META} aria-busy="true">
        Loading jobs…
      </p>
    );
  }
  if (poll.result.state === 'error') return <Callout role="alert">{poll.result.message}</Callout>;
  const detail: CiRunDetail = poll.result.data;
  const countText = `${detail.case_counts.running} running · ${detail.case_counts.passed} passed · ${detail.case_counts.failed} failed · ${detail.case_counts.skipped} skipped`;
  return (
    <div className="grid gap-2.5" data-run-detail={run.id}>
      {detail.jobs.length === 0 ? (
        <p className={META}>No jobs yet.</p>
      ) : (
        detail.jobs.map((j) => <JobCard key={j.id} job={j} now={now} />)
      )}
      <section className="grid gap-1.5" aria-label="Test cases">
        <div className="flex flex-wrap items-center justify-between gap-1.5">
          <b className="text-[12px] font-semibold text-ink-display">Test cases</b>
          <span className={META}>{detail.case_reporting === 'unavailable' ? 'Reporter data unavailable' : detail.case_reporting === 'incomplete' ? 'Reporter data incomplete' : countText}</span>
        </div>
        {detail.case_reporting === 'unavailable' ? (
          <p className={META}>No case results were reported for this run. GitHub job results remain authoritative.</p>
        ) : (
          <>
            {detail.case_reporting === 'incomplete' && <p className={META}>{countText} · results may still be arriving.</p>}
            <ol className="m-0 grid gap-1 p-0 text-[12px]">
              {detail.cases.map((testCase) => (
                <li key={`${testCase.suite}:${testCase.case_id}`} className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2 rounded border border-hairline px-2 py-1.5">
                  <Badge tone={testCase.state === 'failed' ? 'danger' : testCase.state === 'passed' ? 'success' : testCase.state === 'running' ? 'signal' : 'muted'}>{testCase.state}</Badge>
                  <span className="min-w-0 break-words" title={testCase.name}>{testCase.name}</span>
                  <span className={cn(META, 'whitespace-nowrap text-right')}>{testCase.duration_ms == null ? '' : `${(testCase.duration_ms / 1000).toFixed(2)}s`}</span>
                  {testCase.failure_summary && <span className="col-span-3 min-w-0 whitespace-pre-wrap break-words text-danger-ink">{testCase.failure_summary}</span>}
                </li>
              ))}
            </ol>
          </>
        )}
      </section>
      <span className={META}>
        {detail.workflow ?? 'Workflow'} #{detail.run_number ?? detail.run_id}
        {detail.attempt > 1 && ` (attempt ${detail.attempt})`}
        {detail.event && ` · ${detail.event}`}
        {detail.actor && ` · ${detail.actor}`}
        {detail.html_url && (
          <>
            {' · '}
            <a className="text-accent-display" href={detail.html_url} target="_blank" rel="noreferrer">
              Run on GitHub ↗
            </a>
          </>
        )}
      </span>
    </div>
  );
}

// ------------------------------------------------------------------ runs: table and phone cards

function RunCard({ run, ws, now, opened }: { run: CiRun; ws: number; now: number; opened?: boolean }) {
  const [open, setOpen] = useState(Boolean(opened));
  return (
    <li className={cn('grid gap-1.5 border-t border-hairline px-3.5 py-3 first:border-t-0', run.superseded && 'opacity-60')} data-run={run.run_id}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-medium text-ink-display" title={run.repo}>
          {short(run.repo)}
        </span>
        <ResultBadge result={run.result} superseded={run.superseded} stalled={run.stalled} />
      </div>
      <Branch run={run} ws={ws} />
      <Commit run={run} />
      <div className="flex items-center justify-between gap-2">
        <span className={cn(META, 'min-w-0 truncate')}>
          {run.runners.length > 0 && <span className={mono}>{run.runners.join(', ')} · </span>}
          {runTook(run, now)} · {ago(run.created_at, now)}
        </span>
        <button
          type="button"
          className={cn(buttonClass('ghost', 'sm'), 'flex-none')}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide jobs' : 'Jobs'}
        </button>
      </div>
      {open && (
        <div className="rounded bg-surface p-2.5">
          <RunDetail ws={ws} run={run} now={now} />
        </div>
      )}
    </li>
  );
}

function RunsTable({ runs, ws, now, opened, label = 'Recent runs' }: { runs: CiRun[]; ws: number; now: number; opened: number | null; label?: string }) {
  return (
    <Table aria-label={label}>
      <thead>
        <tr>
          <Th className="w-8">
            <span className="sr-only">Jobs</span>
          </Th>
          <Th>Repository</Th>
          <Th>Branch</Th>
          <Th>Commit</Th>
          <Th>Result</Th>
          <Th>Runner</Th>
          <Th className="text-right">Took</Th>
          <Th className="text-right">When</Th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <ExpandableRow
            key={r.id}
            label={`jobs of ${short(r.repo)} ${r.branch ?? ''} run ${r.run_number ?? r.run_id}`}
            dimmed={r.superseded}
            defaultOpen={r.id === opened}
            data-run={r.run_id}
            cells={[
              <span className="whitespace-nowrap" title={r.repo}>
                {short(r.repo)}
              </span>,
              <span className="block max-w-[260px]">
                <Branch run={r} ws={ws} />
              </span>,
              <span className="block max-w-[300px]">
                <Commit run={r} />
              </span>,
              <ResultBadge result={r.result} superseded={r.superseded} stalled={r.stalled} />,
              <span className={cn(mono, 'text-[11.5px]')}>{r.runners.join(', ') || '–'}</span>,
              <span className="block whitespace-nowrap text-right tabular-nums">{runTook(r, now)}</span>,
              <span className="block whitespace-nowrap text-right text-ink-muted">{ago(r.created_at, now)}</span>,
            ]}
            detail={<RunDetail ws={ws} run={r} now={now} />}
          />
        ))}
      </tbody>
    </Table>
  );
}

/**
 * The run an alert's link names (?run=<board id>) when it is not on the loaded page of runs (an
 * older run, or one the filters hide): fetched by id and shown expanded above the list.
 */
function LinkedRun({ ws, id, now }: { ws: number; id: number; now: number }) {
  const [result] = useLoad(
    () => api.GET('/api/workspaces/{ws_id}/fleet/ci/runs/{run_id}', { params: { path: { ws_id: ws, run_id: id } } }),
    `${ws}:${id}`,
  );
  if (result.state === 'loading') return null;
  if (result.state === 'error') {
    return (
      <Callout tone="warning" role="status">
        The linked run is not here any more.
      </Callout>
    );
  }
  const run: CiRun = result.data;
  return (
    <div className="grid gap-1.5" data-linked-run={run.id}>
      <span className={META}>Linked run</span>
      <div className="phone:hidden">
        <RunsTable runs={[run]} ws={ws} now={now} opened={run.id} label="Linked run" />
      </div>
      <ul className="m-0 hidden list-none rounded-lg border border-hairline p-0 phone:block" aria-label="Linked run">
        <RunCard run={run} ws={ws} now={now} opened />
      </ul>
    </div>
  );
}

function Runs({ ws, now }: { ws: number; now: number }) {
  // ?run=<board id> (an alert's link): that run starts expanded, wherever it is.
  const opened = Number(useSearchParams()[0].get('run')) || null;
  const [repo, setRepo] = useState('');
  const [filter, setFilter] = useState('all');
  const [limit, setLimit] = useState(PAGE);
  const poll = usePoll(
    () =>
      api.GET('/api/workspaces/{ws_id}/fleet/ci/runs', {
        params: {
          path: { ws_id: ws },
          query: { repo: repo || undefined, result: filter === 'all' ? undefined : (filter as Filter), limit },
        },
      }),
    `${ws}|${repo}|${filter}|${limit}`,
    5000,
  );
  const data = poll.result.state === 'ok' ? poll.result.data : null;
  const linked = opened !== null && data !== null && !data.runs.some((r) => r.id === opened) ? opened : null;
  const refused = (data?.repos ?? []).filter((r) => r.status === 403);
  return (
    <section className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2.5" aria-labelledby="ci-runs">
      <div className="flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2">
        <h2 id="ci-runs" className="m-0 text-[15px] font-semibold text-ink-display">
          Recent runs
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Repository"
            value={repo}
            onChange={(e) => {
              setRepo(e.target.value);
              setLimit(PAGE);
            }}
          >
            <option value="">All repos</option>
            {(data?.repos ?? []).map((r) => (
              <option key={r.repo} value={r.repo}>
                {r.repo}
              </option>
            ))}
          </Select>
          <Tabs
            tabs={FILTERS}
            value={filter}
            onValueChange={(id) => {
              setFilter(id);
              setLimit(PAGE);
            }}
            label="Result"
            idPrefix="ci-result"
          />
        </div>
      </div>
      {refused.length > 0 && (
        <Callout tone="warning" role="status">
          <span>
            GitHub refused the runs of {refused.map((r) => r.repo).join(', ')}: the App needs <b>Actions: read</b> there.{' '}
            <Link to={settingsSectionPath(ws, 'github')}>Check in Settings</Link>
          </span>
        </Callout>
      )}
      {linked !== null && <LinkedRun ws={ws} id={linked} now={now} />}
      {poll.result.state === 'loading' ? (
        <p className="text-ink-muted" aria-busy="true">
          Loading…
        </p>
      ) : poll.result.state === 'error' ? (
        <Callout role="alert">{poll.result.message}</Callout>
      ) : poll.result.data.runs.length === 0 ? (
        <EmptyState title={repo || filter !== 'all' ? 'No runs match' : 'No CI runs yet'}>
          {repo || filter !== 'all'
            ? 'Try another repository or result.'
            : 'Runs of every repo the GitHub App is installed on appear here within a minute of starting.'}
        </EmptyState>
      ) : (
        <>
          <div className="phone:hidden">
            <RunsTable runs={poll.result.data.runs} ws={ws} now={now} opened={opened} />
          </div>
          <ul className="m-0 hidden list-none rounded-lg border border-hairline p-0 phone:block" aria-label="Recent runs">
            {poll.result.data.runs.map((r) => (
              <RunCard key={r.id} run={r} ws={ws} now={now} opened={r.id === opened} />
            ))}
          </ul>
          {poll.result.data.next_cursor && (
            <button type="button" className={cn(buttonClass('secondary', 'sm'), 'justify-self-center')} onClick={() => setLimit(limit + PAGE)}>
              Show more runs
            </button>
          )}
        </>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ trends

function TrendCard({ t }: { t: CiTrend }) {
  const drift = t.drift_s !== null && Math.abs(t.drift_s) >= 5 ? t.drift_s : null;
  return (
    <div className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-hairline px-3.5 py-3" data-trend={`${t.repo}/${t.job}`}>
      <div className="flex items-baseline justify-between gap-2">
        <b className="min-w-0 truncate font-semibold text-ink-display">{t.job}</b>
        <span className={cn(META, 'min-w-0 truncate')} title={`${t.repo} · ${t.workflow ?? ''}`}>
          {short(t.repo)}
          {t.workflow ? ` · ${t.workflow}` : ''}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2.5">
        <span className="text-[18px] font-semibold tabular-nums text-ink-display">{took(t.median_s)}</span>
        <span className={META}>median</span>
        {drift !== null && (
          <span className={cn('text-[12px]', drift > 0 ? 'text-warning-ink' : 'text-success-ink')}>
            {drift > 0 ? `+${took(drift)} slower lately` : `${took(-drift)} faster lately`}
          </span>
        )}
      </div>
      <Bars
        bars={t.bars.map((b) => ({
          value: b.seconds ?? 0,
          ok: b.passed,
          title: `${took(b.seconds)} · ${b.conclusion}${b.branch ? ` · ${b.branch}` : ''}`,
        }))}
        label={`${t.job}: one bar per run, its height how long it took; ${t.runs - t.passed} of ${t.runs} did not pass`}
      />
      <span className={META}>
        {Math.round(t.pass_rate * 100)}% passed ({t.passed} of {t.runs}) · bar height is how long the run took
      </span>
    </div>
  );
}

function Trends({ ws }: { ws: number }) {
  const [days, setDays] = useState('30');
  const poll = usePoll(
    () => api.GET('/api/workspaces/{ws_id}/fleet/ci/trends', { params: { path: { ws_id: ws }, query: { days: Number(days) as 7 | 30 | 90 } } }),
    `${ws}|${days}`,
    60_000,
  );
  return (
    <section className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-2.5" aria-labelledby="ci-trends">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="ci-trends" className="m-0 text-[15px] font-semibold text-ink-display">
          Trends
        </h2>
        <Tabs tabs={WINDOWS} value={days} onValueChange={setDays} label="Window" idPrefix="ci-days" />
      </div>
      {poll.result.state === 'loading' ? (
        <p className="text-ink-muted" aria-busy="true">
          Loading…
        </p>
      ) : poll.result.state === 'error' ? (
        <Callout role="alert">{poll.result.message}</Callout>
      ) : poll.result.data.jobs.length === 0 ? (
        <p className={META}>No finished jobs in the last {days} days.</p>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-2.5 phone:grid-cols-1">
          {poll.result.data.jobs.map((t) => (
            <TrendCard key={`${t.repo}|${t.workflow}|${t.job}`} t={t} />
          ))}
        </div>
      )}
      <p className={META}>Per job, never per run. Cancelled and skipped jobs count neither as passed nor as failed.</p>
    </section>
  );
}

export function CiTab({ ws, fleet, now }: { ws: number; fleet: Fleet | null; now: number }) {
  return (
    <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-5">
      <RunnerStrip runners={fleet?.runners ?? []} />
      <Runs ws={ws} now={now} />
      <Trends ws={ws} />
    </div>
  );
}
